// The chat overlay: one conversation with the target. Each message starts a run; its steps stream in as one row
// each (click a row for the tool's answer and the screen it saw). Docked right by default; drag the header to float
// it, the left edge to resize, or collapse it to a rail.
import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api, type Conversation, type Verdict } from "./api";
import type { Live } from "./live";
import { ContextMeter } from "./ContextMeter";
import { Markdown } from "./markdown";
import { CleanupRow, StartupRow, StepRow, ThinkingRow } from "./rows";
import { runsOf, type Item, type Run } from "./timeline";

const MAX_ROWS = 300;                                 // drawn at once; earlier ones behind "Show earlier"

type Layout = { mode: "docked" | "floating" | "rail"; w: number; x: number; y: number; h: number; opacity: number };
const LAYOUT_KEY = "vk.chat";
const loadLayout = (): Layout => {
  try { return { mode: "docked", w: 340, x: 40, y: 40, h: 520, opacity: 1, ...JSON.parse(localStorage.getItem(LAYOUT_KEY) || "{}") }; }
  catch { return { mode: "docked", w: 340, x: 40, y: 40, h: 520, opacity: 1 }; }
};

// What start-up is doing (Session._part), in the chat while a session starts
const PHASES: Record<string, string> = {
  pointer: "Finding the pointer",
  curve: "Measuring how the pointer moves (once per screen size, about a minute)",
  background: "Watching what moves on screen",
  home: "Placing the pointer",
};

function when(t: number): string {
  const d = new Date(t * 1000), now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

export function Chat({ live, targetId, brain, canChat, onDock }: { live: Live; targetId: string; brain: string; canChat: string | null;
  onDock: (px: number) => void }) {
  const [layout, setLayout] = useState<Layout>(loadLayout);
  const [convs, setConvs] = useState<Conversation[]>([]);
  const [cid, setCid] = useState<string | null>(null);
  const [earlier, setEarlier] = useState(0);         // extra rows shown above the last MAX_ROWS
  const [draft, setDraft] = useState("");
  const [pending, setPending] = useState<string | null>(null);   // sent, its run not started yet
  const [showConvs, setShowConvs] = useState(false);
  const [now, setNow] = useState(Date.now());
  const body = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const running = live.state === "running" || live.state === "preparing" || live.state === "stopping"
    || (live.state === "human" && !!live.run);

  useEffect(() => { try { localStorage.setItem(LAYOUT_KEY, JSON.stringify(layout)); } catch { /* private mode */ } }, [layout]);
  useEffect(() => { onDock(layout.mode === "docked" ? layout.w : layout.mode === "rail" ? 28 : 0); }, [layout.mode, layout.w, onDock]);
  const convKey = `vk.conv.${targetId}`;
  const loadConvs = () => api.conversations(targetId).then(setConvs).catch(() => setConvs([]));
  useEffect(() => {                                   // a target opens on the chat last open on it
    let last: string | null = null;
    try { last = localStorage.getItem(convKey); } catch { /* private mode */ }
    setCid(last); loadConvs();
  }, [targetId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { try { cid ? localStorage.setItem(convKey, cid) : localStorage.removeItem(convKey); } catch { /* private mode */ } }, [cid, convKey]);
  useEffect(() => { loadConvs(); }, [live.run, live.state]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (live.conversation) setCid(live.conversation); }, [live.conversation]);
  useEffect(() => {                                   // the open chat's history (again after a reset)
    if (!cid) return;
    let gone = false;
    api.conversation(cid).then((c) => {
      if (gone) return;
      live.loadHistory(cid, c.runs);
      if (c.ctx) { try { live.setConversationCtx(JSON.parse(c.ctx)); } catch { /* an old row */ } }
    }).catch(() => { if (!gone) setCid(null); });
    return () => { gone = true; };
  }, [cid, live.historyWanted]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setEarlier(0); }, [cid]);
  useEffect(() => { if (!running) return; const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, [running]);

  const runs = useMemo(() => runsOf(live.timeline, cid), [live.timeline, cid]);
  // draw at most MAX_ROWS rows (plus what "Show earlier" opened): whole runs, newest kept
  const shown = useMemo(() => {
    let rows = 0, i = runs.length;
    while (i > 0 && rows + runs[i - 1].items.length + 2 <= MAX_ROWS + earlier) { rows += runs[i - 1].items.length + 2; i--; }
    return { from: Math.min(i, Math.max(0, runs.length - 1)), hidden: Math.min(i, Math.max(0, runs.length - 1)) };
  }, [runs, earlier]);
  // follow new rows only while the reader is at the bottom; scrolling up or opening a row stops it, and a pill
  // says there is more below
  const follow = useRef(true);
  const [unseen, setUnseen] = useState(false);
  const toBottom = () => { const b = body.current; if (b) b.scrollTop = b.scrollHeight; follow.current = true; setUnseen(false); };
  useLayoutEffect(() => {
    if (follow.current) { const b = body.current; if (b) b.scrollTop = b.scrollHeight; }
    else setUnseen(true);
  }, [runs, pending]);
  useEffect(() => { toBottom(); }, [cid]);   // eslint-disable-line react-hooks/exhaustive-deps
  const onScroll = () => {
    const b = body.current;
    if (!b) return;
    const at = b.scrollHeight - b.scrollTop - b.clientHeight < 40;
    follow.current = at;
    if (at) setUnseen(false);
  };

  useEffect(() => {                                   // the box grows with the text, up to ~7 lines
    const t = input.current;
    if (!t) return;
    t.style.height = "auto";
    t.style.height = `${Math.min(t.scrollHeight + 2, 140)}px`;
  }, [draft]);

  // A message shows the moment it is sent; it becomes its run when the server starts it, and goes back into the box
  // if the server refused it or never answered.
  useEffect(() => { setPending(null); }, [live.run]);
  useEffect(() => { if (live.error) setPending((p) => { if (p) setDraft(p); return null; }); }, [live.error]);
  useEffect(() => {
    if (!pending) return;
    const t = setTimeout(() => { setDraft(pending); setPending(null); }, 20000);
    return () => clearTimeout(t);
  }, [pending]);

  const busy = running || !!pending;                  // a turn: from Enter until its run ends (Stop all along)
  const mcp = live.driver === "mcp";                  // a person's own Claude Code drives: the chat is read-only
  const [stopAsked, setStopAsked] = useState(false);
  const stopping = stopAsked || live.state === "stopping";
  useEffect(() => { if (!busy) setStopAsked(false); }, [busy]);
  const stop = () => {
    if (!busy || stopping) return;
    setStopAsked(true);
    live.send({ t: "stop" });
  };
  useEffect(() => {                                   // Esc stops the turn (not while the PC is taken over)
    if (!busy) return;
    const k = (e: KeyboardEvent) => { if (e.key === "Escape" && live.state !== "human" && !document.pointerLockElement) stop(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  });

  const send = () => {
    const text = draft.trim();
    if (!text || busy || canChat) return;
    if (!live.send({ t: "chat", text, conversation: cid, brain })) return;   // reconnecting: the text stays
    setPending(text);
    setDraft("");
  };
  const newChat = () => { setCid(null); setShowConvs(false); };

  // dragging and resizing
  const drag = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest("button")) return;
    const sx = e.clientX, sy = e.clientY, start = layout;
    const mv = (ev: PointerEvent) => {
      const dx = ev.clientX - sx, dy = ev.clientY - sy;
      if (start.mode === "docked" && Math.abs(dx) + Math.abs(dy) > 12) {
        setLayout({ ...start, mode: "floating", x: Math.max(0, window.innerWidth - start.w - 40), y: 40 });
      } else if (start.mode === "floating") {
        setLayout({ ...start, x: Math.max(0, start.x + dx), y: Math.max(0, start.y + dy) });
      }
    };
    const upH = () => { window.removeEventListener("pointermove", mv); window.removeEventListener("pointerup", upH); };
    window.addEventListener("pointermove", mv); window.addEventListener("pointerup", upH);
  };
  const resize = (e: React.PointerEvent) => {
    e.stopPropagation();
    const sx = e.clientX, start = layout;
    const mv = (ev: PointerEvent) => setLayout({ ...start, w: Math.min(560, Math.max(280, start.w - (ev.clientX - sx))) });
    const upH = () => { window.removeEventListener("pointermove", mv); window.removeEventListener("pointerup", upH); };
    window.addEventListener("pointermove", mv); window.addEventListener("pointerup", upH);
  };

  // the context meter sits just outside the chat's top-left corner, wherever the chat is (docked, floating, rail)
  const ctx = cid ? live.ctx[cid] : undefined;
  const meter = ctx && live.state !== "human"
    ? <ContextMeter ctx={ctx} style={{ right: "calc(100% + 8px)", top: layout.mode === "floating" ? 0 : 6 }} /> : null;

  if (layout.mode === "rail") {
    return (
      <div className="rail" onClick={() => setLayout({ ...layout, mode: "docked" })} title="Open the chat">
        {meter}
        {running ? <span className="spin" /> : <span className="dot" />}
        <span className="label">Chat</span>
      </div>
    );
  }

  const last = runs.at(-1);
  const elapsed = running && last && !last.status ? Math.max(0, Math.round((Math.max(now, Date.now()) - last.started) / 1000)) : null;
  const style: React.CSSProperties = layout.mode === "docked"
    ? { width: layout.w, opacity: layout.opacity }
    : { left: layout.x, top: layout.y, width: layout.w, height: layout.h, opacity: layout.opacity };
  const title = convs.find((c) => c.id === cid)?.title || (cid ? "Conversation" : "New chat");

  return (
    <div className={`chat ${layout.mode}`} style={style}>
      {meter}
      {layout.mode === "docked" && <div className="resize" onPointerDown={resize} />}
      <div className="chat-head" onPointerDown={drag}>
        <div className="menu title">
          <button className="picker" style={{ paddingLeft: 0 }} onClick={() => { if (!showConvs) loadConvs(); setShowConvs((v) => !v); }}>
            <span className="label">Chat</span><span className="name muted">{title}</span><span className="caret">▼</span>
          </button>
          {showConvs && (
            <div className="menu-list conv-list" onMouseLeave={() => setShowConvs(false)}>
              <button className="menu-item" onClick={newChat}>+ New chat</button>
              {convs.length > 0 && <div className="menu-rule" />}
              {convs.map((c) => (
                <button key={c.id} className={`menu-item ${c.id === cid ? "on" : ""}`} onClick={() => { setCid(c.id); setShowConvs(false); }}>
                  <span className="grow" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.title || "Untitled"}</span>
                  <span className="when">{when(c.updated_at)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <button className="btn quiet" title="Opacity" onClick={() => setLayout({ ...layout, opacity: layout.opacity > 0.7 ? 0.6 : 1 })}>◐</button>
        {layout.mode === "floating" && <button className="btn quiet" title="Dock right" onClick={() => setLayout({ ...layout, mode: "docked" })}>⇥</button>}
        <button className="btn quiet" title="Collapse" onClick={() => setLayout({ ...layout, mode: "rail" })}>–</button>
      </div>

      <div className="chat-body" ref={body} onScroll={onScroll}
        onClickCapture={(e) => { if ((e.target as HTMLElement).closest(".step[role=button]")) follow.current = false; }}>
        {runs.length === 0 && !pending && (
          <div className="msg-text muted">Tell the agent what to do on this computer. Each message is one task; it can see
            the screen and use the mouse and keyboard. You can take over at any time.</div>
        )}
        {shown.hidden > 0 && (
          <button className="btn quiet earlier" onClick={() => setEarlier((e) => e + MAX_ROWS)}>Show earlier ({shown.hidden})</button>
        )}
        {runs.slice(shown.from).map((r) => (
          <RunView key={r.id} r={r} live={!r.status && r === runs.at(-1)} brain={brain}
            preparing={!r.status && r === runs.at(-1) && live.state === "preparing"} phase={live.phase} />
        ))}
        {pending && (
          <div>
            <div className="msg-user">{pending}</div>
            <div className="step"><span className="spin" /><span className="arg">Sending…</span></div>
          </div>
        )}
      </div>

      {unseen && (
        <button className="new-pill" onClick={toBottom}>
          <svg viewBox="0 0 12 12" width="11" height="11" aria-hidden><path d="M6 2.5v7M2.8 6.6 6 9.8l3.2-3.2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
          New activity
        </button>
      )}
      <div className="chat-foot">
        {mcp ? <div className="note">Claude Code (MCP) is driving this computer: you can watch, take over or stop it.</div>
          : canChat && <div className="note">{canChat}</div>}
        <div className="composer">
          <button className="cbtn plus" title="New chat" disabled={busy} onClick={newChat}>
            <svg viewBox="0 0 12 12" aria-hidden><path d="M6 1.5v9M1.5 6h9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </button>
          <textarea
            ref={input}
            rows={1}
            value={draft}
            placeholder={busy ? `${stopping ? "Stopping" : pending && !running ? "Sending" : mcp ? "Claude Code (MCP) is driving" : live.state === "preparing" ? "Getting ready" : live.state === "human" ? "Paused: you have control" : "Working"}`
              + `${last && !last.status && last.lastLabel ? ` · ${last.lastLabel}` : ""}`
              + `${elapsed != null ? ` · ${elapsed} s` : ""}${last ? ((n: number) => ` · ${n} step${n === 1 ? "" : "s"}`)(last.items.filter((i: Item) => i.kind === "tool").length) : ""}`
              : canChat ? "" : `Message ${brain === "codex" ? "Codex" : "Claude"}`}
            disabled={!!canChat || mcp}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          />
          {busy ? (
            <button className={`cbtn send stop ${stopping ? "stopping" : ""}`} title={stopping ? "Stopping…" : "Stop (Esc)"}
              disabled={stopping} onClick={stop}>
              {stopping ? <span className="spin light" />
                : <svg viewBox="0 0 12 12" aria-hidden><rect x="3" y="3" width="6" height="6" rx="1" fill="currentColor" /></svg>}
            </button>
          ) : (
            <button className="cbtn send" title="Send (Enter)" disabled={!draft.trim() || !!canChat} onClick={send}>
              <svg viewBox="0 0 12 12" aria-hidden><path d="M6 10V2.5M2.5 5.5 6 2l3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// One run: the message, its timeline in order, its result. Memoized: a new event for another run (or another row)
// does not redraw it.
const RunView = memo(function RunView({ r, live, preparing, phase, brain }: { r: Run; live: boolean; preparing: boolean;
  phase: string | null; brain: string }) {
  return (
    <div className="run">
      <div className="msg-user">{r.message}</div>
      <StartupRow run={r} stage={r.stages.startup} readyS={r.readyS} />
      {r.items.map((it, idx) => {
        if (it.kind === "text") {
          // the model's last words are usually its answer, shown below as the run's result: not twice. Words written
          // after finish only restate that result (runs kept before 2026-10-23 still have them)
          const isAnswer = r.status && idx === r.items.length - 1 && it.text.trim() === (r.summary || "").trim();
          const afterFinish = r.items.slice(0, idx).some((x) => x.kind === "tool" && x.tool === "finish");
          return isAnswer || afterFinish || !it.text.trim() ? null
            : <Markdown key={it.n} className={`msg-text ${it.streaming ? "streaming" : ""}`} text={it.text} streaming={!!it.streaming && !r.status} />;
        }
        return <StepRow key={it.n} s={it} run={r} live={live} />;
      })}
      {live && <ThinkingRow run={r} brain={brain} />}
      {live && <CleanupRow run={r} />}
      {preparing && !r.stages.startup && (            // a server without the activity stream (protocol 1)
        <div className="step"><span className="spin" /><span className="arg">{PHASES[phase ?? ""] ?? "Getting ready"}</span></div>
      )}
      {r.status && (
        <div className="msg-end">
          <span className={`dot ${r.status === "passed" ? "ok" : r.status === "stopped" ? "warn" : r.status === "ended" ? "" : "err"}`} />
          <Markdown className="summary" text={r.summary || r.status} />
        </div>
      )}
      {r.status && r.status !== "running" && <VerdictBar rid={r.id} verdict={r.verdict ?? null} note={r.note ?? ""} />}
    </div>
  );
});

// The person's judgement of a run: did it do what they asked? (the agent's own "passed" is its opinion). A note
// says why; it is what the Runs page and the training export judge by.
const THUMB = "M7 10v11H3V10h4zm2 11h8.3a2 2 0 0 0 2-1.6l1.4-7A2 2 0 0 0 18.7 10H14l.8-4.2A1.6 1.6 0 0 0 13.2 4L9 10v11z";

function VerdictBar({ rid, verdict, note }: { rid: string; verdict: Verdict | null; note: string }) {
  const [v, setV] = useState<Verdict | null>(verdict);
  const [n, setN] = useState(note);
  const [editing, setEditing] = useState(false);
  const save = (nv: Verdict | null, nn: string) =>
    api.verdict(rid, nv, nn).then((r) => { setV(r.verdict); setN(r.note); }).catch(() => undefined);
  const pick = (which: Verdict) => {
    const nv = v === which ? null : which;
    setV(nv);
    setEditing(nv !== null && !n);
    void save(nv, nv ? n : "");
  };
  return (
    <div className="verdict">
      {(["up", "down"] as const).map((w) => (
        <button key={w} className={`thumb ${v === w ? "on" : ""}`} title={w === "up" ? "Did what I asked" : "Did not"}
          aria-pressed={v === w} onClick={() => pick(w)}>
          <svg viewBox="0 0 24 24" width="13" height="13" style={w === "down" ? { transform: "rotate(180deg)" } : undefined}>
            <path d={THUMB} fill="currentColor" />
          </svg>
        </button>
      ))}
      {editing ? (
        <input className="verdict-note" autoFocus placeholder={v === "down" ? "What went wrong? (optional)" : "Anything to add? (optional)"}
          defaultValue={n} maxLength={2000}
          onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") setEditing(false); }}
          onBlur={(e) => { setEditing(false); if (e.target.value !== n) void save(v, e.target.value); }} />
      ) : n ? (
        <span className="verdict-text" onClick={() => setEditing(true)} title="Edit the note">{n}</span>
      ) : v ? (
        <button className="btn quiet verdict-add" onClick={() => setEditing(true)}>Add a note</button>
      ) : null}
    </div>
  );
}
